import React from "react";
import { Collapse, Space, Tooltip } from "antd";
import { FolderIcon } from "@heroicons/react/24/solid";
import {
  mapping,
  statusColors,
} from "../base-requirements/requirements/fixtures/mapping.tsx";
import {
  Folder,
  Requirement,
  Subfolder,
} from "../../redux/interfaces/folder.types.ts";
import { requirementTypeMapping } from "../base-requirements/requirements/fixtures/requirementTypeMapping.tsx";
import { setActiveRequirement } from "../../redux/slices/requirementsSlice.ts";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import { lightBlue } from "../../utils/theme/colors.ts";
import { getSubfoldersMapByIdToSlug } from "../../utils/subfolders/subfoldersMapByIdToSlug.ts";
import { getRequirementNameBySlug } from "../../utils/base-requirements/requirements/slugMap.ts";
import { getSameLabelOrdinal } from "../../utils/subfolders/roleOrdinal.ts";

interface Props {
  activeRequirement: Requirement | null;
  activeFolder: Folder | null;
  groupRequirements: any;
}

const { Panel } = Collapse;

export const CollapseFolderRequirements: React.FC<Props> = ({
  activeRequirement,
  groupRequirements,
}) => {
  const dispatch = useAppDispatch();
  const { activeFolder } = useAppSelector((state) => state.folder);
  const { groupType } = useAppSelector((state) => state.requirements);
  const subfolderSlugMap = getSubfoldersMapByIdToSlug(activeFolder);

  // Map subfolder.id -> subfolder so the "Por subcarpeta" headers can be resolved
  // per participant (group keys are subfolder ids, not slugs).
  const allSubfolders: Subfolder[] = activeFolder?.subfolders || [];
  const subfolderById: Record<string, Subfolder> = {};
  allSubfolders.forEach((sf) => {
    subfolderById[sf.id] = sf;
  });

  const subfolderBaseTitle = (sf: Subfolder): string =>
    mapping[sf.slug]?.title || sf.slug;

  const getGroupHeader = (group: string): string => {
    if (groupType === "subfolder") {
      const subfolder = subfolderById[group];
      if (subfolder) {
        const baseTitle = subfolderBaseTitle(subfolder);
        const fullName = subfolder.metadata?.full_name;
        if (fullName) return `${baseTitle} — ${fullName}`;
        // Number by role instead of `person_index`: DocVault numbers per-template,
        // so the principal and the first same-role additional can both be
        // person_index === 1 and would otherwise render identically.
        const ordinal = getSameLabelOrdinal(
          subfolder,
          allSubfolders,
          subfolderBaseTitle,
        );
        if (ordinal) return `${baseTitle} ${ordinal}`;
        return baseTitle;
      }
    }
    return mapping[group]?.title || group;
  };

  const onActiveRequirementChange = (req: Requirement) => {
    dispatch(setActiveRequirement(req));
  };

  return (
    <>
      <Collapse
        accordion
        className={`bg-white border-none ${activeRequirement ? "w-5/12" : "w-full"}`}
        style={{ border: "none" }}
      >
        {Object.entries(groupRequirements).map(
          ([group, data]: [string, any], index) => (
            <Panel
              key={index}
              header={
                <Space>
                  <FolderIcon className="h-5 w-5" color={lightBlue} />
                  {`${getGroupHeader(group)}
                    (${data.flat().length})`}
                </Space>
              }
              className={`border-none bg-white shadow-none h-fit`}
            >
              {data.map((req: Requirement, idx: number) => {
                const reqType = req.form_values ? "form" : "file";
                const reqName =
                  groupType === "status"
                    ? getRequirementNameBySlug(
                        subfolderSlugMap[req.subfolder],
                        req.slug,
                        req.name,
                      )
                    : req.name;

                const color = statusColors[req.status];
                const iconMap = requirementTypeMapping(color)[reqType];

                const status = mapping[req.status].title;
                const icon = iconMap.icon;
                const text = `${iconMap.title} - ${status}`;

                return (
                  <Space
                    key={idx}
                    className={`w-full flex flex-row cursor-pointer border-none ${activeRequirement?.id === req.id ? "bg-gray-300" : ""}`}
                    onClick={() => onActiveRequirementChange(req)}
                  >
                    <Tooltip title={text}>{icon}</Tooltip>
                    <span>{reqName}</span>
                  </Space>
                );
              })}
            </Panel>
          ),
        )}
      </Collapse>
    </>
  );
};
